import { z } from 'zod';
import { uuidSchema } from './validation.js';
import { costCurrencySchema } from './cost-currency.js';
import { BASE_GARMENT_MAX_VARIANTS, baseGarmentAudienceSchema, baseGarmentAxisSchema, baseGarmentCodeSchema, baseGarmentNameSchema, refineAudienceClient, sizeColourCodeSchema, } from './base-garments.js';
/**
 * Supplier catalog and Supply (BLU-1268). Inputs only: the organisation and the
 * user always come from auth, so no schema here carries an organizationId (zod's
 * default strip drops one).
 *
 * Supply is the operator word for `sku_inventory.stock_mode`, one value per
 * (blank, location): `stocked` (counted on the shelf), `just_in_time` (stock is
 * not tracked; each batch makes a shopping list) and `managed_elsewhere` (the
 * store quantity is not ours to push, or is a fixed number). Stock type is a
 * different thing (the audience of a product code) and is not touched here.
 */
export const STOCK_MODES = ['stocked', 'just_in_time', 'managed_elsewhere'];
export const stockModeSchema = z.enum(STOCK_MODES);
/** Managed elsewhere is never set when a blank is added (Settings of the owner only). */
export const ADD_STOCK_MODES = ['stocked', 'just_in_time'];
export const addStockModeSchema = z.enum(ADD_STOCK_MODES);
/**
 * The refusal keys of the supplier catalog and Supply RPCs, of the two
 * supplier link conflicts (raised by the service, not the SQL) and of the Order
 * and wait RPCs (BLU-1506), the one list the
 * backend (`lib/supplier-catalog-errors.ts`) and the browser both read. Each
 * rides as `SUPPLIER_CATALOG_<KEY>` in the backend's `FORWARDABLE_CAUSE_CODES`
 * (`trpc.ts`), which keeps its literal list; a backend test pins every key into it.
 */
export const SUPPLIER_CATALOG_ERROR_KEYS = [
    'SUPPLIER_NOT_LINKED',
    'CATALOG_VARIANT_MISMATCH',
    'CODE_AUDIENCE_MISMATCH',
    'NOT_A_BLANK',
    'NOT_LOCATION_OWNER',
    'PREPRINT_RESERVED',
    'OPEN_TRANSFER',
    'INVALID_INPUT',
    'CATALOG_SUPPLIER_ALREADY_LINKED',
    'SUPPLIER_ALREADY_LINKED',
    // BLU-1506 Order and wait (SQLSTATEs P0992 to P0995; P0985 is NOT_LOCATION_OWNER).
    // OAW_PO_NOT_FOUND: no Order and wait PO with that id in the caller's org.
    // OAW_PO_WRONG_STATUS: Send on a PO that is not building, Arrived on one that was not sent.
    // OAW_PO_EMPTY: Send with nothing left to send.
    // SUPPLIER_NOT_FOUND: "When to buy" on a supplier that is not the caller's.
    'OAW_PO_NOT_FOUND',
    'OAW_PO_WRONG_STATUS',
    'OAW_PO_EMPTY',
    'SUPPLIER_NOT_FOUND',
    // BLU-1526 Follow supplier stock: turned on for a supplier set to Order and wait (raised by the service, no SQLSTATE).
    'FOLLOW_NOT_FOR_ORDER_AND_WAIT',
];
// LOCKSTEP: the caps in supabase/migrations/20261023060500_blu1268_supply_rpcs.sql
// (the cells cap: 20261030010000_catalog_add_cap_1000.sql).
export const CATALOG_MAX_IMPORT_ROWS = 5000;
export const CATALOG_MAX_CELLS = BASE_GARMENT_MAX_VARIANTS;
export const SET_SUPPLY_MAX_SKUS = 200;
export const SUPPLY_COUNT_MAX = 999_999;
export const CATALOG_COST_MAX = 999_999.99;
// ── Platform catalog import (Blue Monk only) ──────────────────────────────────────
/** One CSV cell as the browser reads it. The RPC trims it, and an empty cell is absent. */
const catalogCellSchema = z.string().max(2048).optional();
/**
 * One variant row of the platform catalog CSV (camelCase of the CSV header).
 * Deliberately lenient: `catalog_import_apply` is the one validator and answers
 * with the 1-based row, the field and a plain message for every problem, which
 * the preview shows. A shape error here would only say "bad request".
 */
export const catalogImportRowSchema = z.object({
    supplierSlug: catalogCellSchema,
    supplierName: catalogCellSchema,
    supplierCurrency: catalogCellSchema,
    styleCode: catalogCellSchema,
    styleName: catalogCellSchema,
    category: catalogCellSchema,
    suggestedCode: catalogCellSchema,
    styleImageUrl: catalogCellSchema,
    colourName: catalogCellSchema,
    colourHex: catalogCellSchema,
    sizeLabel: catalogCellSchema,
    sizeSort: catalogCellSchema,
    supplierSku: catalogCellSchema,
    listPrice: catalogCellSchema,
    listPriceCurrency: catalogCellSchema,
    weightGrams: catalogCellSchema,
    imageUrl: catalogCellSchema,
    isActive: catalogCellSchema,
});
export const catalogImportSchema = z.object({
    rows: z.array(catalogImportRowSchema).min(1).max(CATALOG_MAX_IMPORT_ROWS),
});
// ── Catalog reads and the supplier link ───────────────────────────────────────────
/**
 * One supplier's style list (no variants). `locationId` (optional) marks
 * which styles the caller org already has a blank with a stock row for at
 * that location.
 */
export const catalogStylesSchema = z.object({ catalogSupplierId: uuidSchema, locationId: uuidSchema.optional() });
/**
 * One style with its variants, read when the picker opens it. `locationId`
 * (optional) marks which variants the caller org already has there.
 */
export const catalogStyleSchema = z.object({ catalogStyleId: uuidSchema, locationId: uuidSchema.optional() });
const normaliseAxisName = (value) => (value ?? '').trim().toLowerCase();
/**
 * What a style offers, by the picker's rule (`styleAxes` in the browser): a
 * colour or size name counts once in any case or spacing; a style whose
 * variants all lack a size is one size (0 sizes); a style that mixes sized and
 * size-less variants keeps its sized ones. The style list counts with this so
 * its "5 colours" matches the chips the style opens to.
 */
export function catalogStyleCounts(variants) {
    const sizeless = variants.length > 0 && variants.every((variant) => normaliseAxisName(variant.sizeLabel) === '');
    const colours = new Set();
    const sizes = new Set();
    for (const variant of variants) {
        const size = normaliseAxisName(variant.sizeLabel);
        if (!sizeless && size === '')
            continue;
        const colour = normaliseAxisName(variant.colourName);
        if (colour === '')
            continue;
        colours.add(colour);
        if (size !== '')
            sizes.add(size);
    }
    return { colours: colours.size, sizes: sizes.size, variants: variants.length };
}
export const catalogLinkSupplierSchema = z.object({
    catalogSupplierId: uuidSchema,
    supplierId: uuidSchema,
});
export const catalogAddToMySuppliersSchema = z.object({ catalogSupplierId: uuidSchema });
// ── Add from catalog ──────────────────────────────────────────────────────────────
/** The picker's pre-submit read of a product code: does the org already have it, and for which stock type. */
export const catalogLookupCodeSchema = z.object({
    code: baseGarmentCodeSchema,
    locationId: uuidSchema,
});
/**
 * One toggled cell of the picker: the catalog variant and the org's own size
 * and colour codes it maps to (`size` null when the style has none).
 */
export const catalogAddCellSchema = z.object({
    catalogVariantId: uuidSchema,
    size: sizeColourCodeSchema.nullable(),
    colour: sizeColourCodeSchema,
    stockMode: addStockModeSchema.optional(),
});
export const catalogAddCostSchema = z.object({
    amount: z.number().min(0).max(CATALOG_COST_MAX),
    /** Omitted = the org currency. */
    currency: costCurrencySchema.optional(),
});
export const catalogAddToLocationSchema = z
    .object({
    locationId: uuidSchema,
    catalogStyleId: uuidSchema,
    /** The org's own supplier row, linked to the style's catalog supplier. */
    supplierId: uuidSchema,
    code: baseGarmentCodeSchema,
    name: baseGarmentNameSchema,
    audience: baseGarmentAudienceSchema,
    clientOrganizationId: uuidSchema.optional(),
    sizes: baseGarmentAxisSchema,
    colours: baseGarmentAxisSchema,
    cells: z.array(catalogAddCellSchema).min(1).max(CATALOG_MAX_CELLS),
    defaultMode: addStockModeSchema,
    cost: catalogAddCostSchema.optional(),
})
    .superRefine((value, context) => {
    refineAudienceClient(value, context);
    const variants = new Set();
    const pairs = new Set();
    value.cells.forEach((cell, index) => {
        if (variants.has(cell.catalogVariantId)) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'A variant is listed twice', path: ['cells', index] });
        }
        variants.add(cell.catalogVariantId);
        const pair = `${cell.size ?? ''}|${cell.colour}`;
        if (pairs.has(pair)) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'A size and colour is listed twice', path: ['cells', index] });
        }
        pairs.add(pair);
    });
    const axisCodes = (axis) => new Set([...axis.codes, ...axis.newCodes.map((entry) => entry.code)]);
    const sizeCodes = axisCodes(value.sizes);
    const colourCodes = axisCodes(value.colours);
    value.cells.forEach((cell, index) => {
        if (value.sizes.none ? cell.size !== null : cell.size === null || !sizeCodes.has(cell.size)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Every cell needs one of the chosen sizes',
                path: ['cells', index, 'size'],
            });
        }
        // No colour: the org's colourless code is only known to the database.
        if (!value.colours.none && !colourCodes.has(cell.colour)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Every cell needs one of the chosen colours',
                path: ['cells', index, 'colour'],
            });
        }
    });
});
